import React, { useRef } from "react";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  useMaterialReactTable,
} from "material-react-table";
import { formatCurrency, formatNumber } from "../../utils/dataCalculations";
import { PageHeader } from "../ui";
import { useAuth } from "../../context/AuthContext";
import ExportToCSVButton from "../ExportToCsvButton";

const TableContainer = ({
  title,
  data,
  columnsConfig,
  globalFilter,
  setGlobalFilter,
  sorting,
  setSorting,
  scrollPosition,
  tableConfig,
  customAction,
}) => {
  const theme = useTheme();
  const tableContainerRef = useRef(null);
  const { claims } = useAuth();
  const roles = claims?.roles || [];

  const table = useMaterialReactTable({
    columns: columnsConfig(theme, globalFilter, formatCurrency, formatNumber, tableConfig.onExpand),
    data,
    ...tableConfig,
    // Columns flex to fit the width: no horizontal scroll, and no pinning
    // (pinned offsets assume fixed column widths).
    layoutMode: "grid",
    enableColumnPinning: false,
    enablePinning: false,
    initialState: { ...tableConfig.initialState, columnPinning: {} },
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    muiTableContainerProps: {
      sx: {
        maxHeight: {
          xs: "calc(100vh - 220px)",
          sm: "calc(100vh - 220px)",
        },
        width: "100%",
        overflowX: "auto",
        overflowY: "auto",
        position: "relative",
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[2],
        minWidth: "100%",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        "&::-webkit-scrollbar": {
          height: "10px",
          width: "10px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.mode === "dark"
            ? theme.palette.grey[800]
            : theme.palette.grey[100],
          borderRadius: theme.shape.borderRadius,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.mode === "dark"
            ? theme.palette.grey[600]
            : theme.palette.grey[400],
          borderRadius: theme.shape.borderRadius,
          border: `2px solid ${theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100]}`,
          "&:hover": {
            backgroundColor: theme.palette.mode === "dark"
              ? theme.palette.grey[500]
              : theme.palette.grey[500],
          },
        },
      },
      ref: tableContainerRef,
    },
    muiTableFooterRowProps: {
      sx: {
        backgroundColor: theme.palette.mode === "dark"
          ? theme.palette.grey[800]
          : theme.palette.grey[100],
        fontWeight: 600,
        position: "sticky",
        bottom: 0,
        zIndex: 10,
        borderTop: `2px solid ${theme.palette.divider}`,
        height: "44px",
        boxShadow: `0 -2px 8px ${theme.palette.mode === "dark" ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.1)"}`,
      },
    },
    muiTableFooterCellProps: {
      sx: {
        borderRight: `1px solid ${theme.palette.divider}`,
        "&:last-of-type": {
          borderRight: "none",
        },
        padding: theme.spacing(1, 1),
        fontSize: "0.8125rem",
      },
    },
  });

  const handleExportToCSV = () => {
    const headers = columnsConfig(
      theme,
      globalFilter,
      formatCurrency,
      formatNumber
    ).map((col) => col.header);
    const csvContent = [
      headers.join(","),
      ...data.map((row) =>
        columnsConfig(theme, globalFilter, formatCurrency, formatNumber)
          .map((col) => {
            const value = row[col.accessorKey];
            if (typeof value === "number") {
              return value;
            }
            return value?.includes(",") ? `"${value}"` : value;
          })
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${title.replace(/\s+/g, "_")}_${
        new Date().toISOString().split("T")[0]
      }.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
        borderRadius: 0,
        boxShadow: theme.shadows[1],
      }}
    >
      <PageHeader
        sx={{ px: "20px", justifyContent: "flex-start", alignItems: "end" }}
        title={title}
        action={
          <Box
            sx={{
              display: "flex",
              alignItems: "bottom", // vertically center items
              justifyContent: "flex-start", // left align
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {/* Search Field */}
            <Box sx={{ maxWidth: "280px", flexShrink: 0 }}>
              <MRT_GlobalFilterTextField
                table={table}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": {
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid ${theme.palette.divider}`,
                    fontSize: "0.875rem",
                    "&:hover": {
                      backgroundColor: theme.palette.background.paper,
                      borderColor: theme.palette.primary.dark,
                    },
                    "&.Mui-focused": {
                      backgroundColor: theme.palette.background.paper,
                      borderColor: theme.palette.primary.dark,
                      boxShadow: `0 0 0 2px ${theme.palette.primary.dark}20`,
                    },
                  },
                  "& .MuiInputBase-input": {
                    padding: theme.spacing(0.75, 1.25),
                    color: theme.palette.text.primary,
                    "&::placeholder": {
                      color: theme.palette.text.secondary,
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>
            <ExportToCSVButton
              columnsConfig={columnsConfig}
              theme={theme}
              globalFilter=""
              formatCurrency={formatCurrency}
              formatNumber={formatNumber}
              data={data}
              filename={title}
            />
            {customAction}
          </Box>
        }
      />

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          backgroundColor: theme.palette.background.default,
          padding: theme.spacing(2),
        }}
      >
        <MaterialReactTable
          table={table}
          muiTablePaperProps={{
            sx: {
              position: "relative",
              boxShadow: "none",
              backgroundColor: "transparent",
              height: "100%", // Ensure table takes full available height
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default TableContainer;
